import { useCallback, useEffect, useMemo } from 'react'

import {
  parseColumnFilters,
  serializeColumnFilters,
  type ColumnFilters,
  type FilterFieldKind,
} from '@/components/filters/column-filter'
import { useQueryState } from '@/hooks/use-query-state'
import {
  parseSelections,
  serializeSelections,
  toggleSelection,
  type Selection,
  type SelectionInput,
} from '@/lib/cross-filter'
import {
  filterKind,
  shortName,
  type ReportDef,
  type ReportField,
  type ReportFilterDef,
} from '@/lib/cube-report'
import { resolveDefaultRange } from '@/lib/date-range'
import {
  isValidPeriod,
  periodRange,
  resolveRelativePeriod,
  type PeriodFilterDef,
} from '@/lib/period'
import type { ReportFilterState } from '@/services/cube/report'

/** Kullanıcı filtrelere dokundu: artık meta varsayılanları değil, adres çubuğu geçerli. */
const TOUCHED_PARAM = 'f'
const NO_EXTRA_KEYS: readonly string[] = []

/** Sütun filtresi uygulanabilen alanlar: tablo raporunun ya da pano listesinin kolonları. */
export function columnFilterKinds(report: ReportDef): Map<string, FilterFieldKind> {
  let dimensions: ReportField[] = []
  if (report.layout === 'table') dimensions = report.dimensions
  if (report.layout === 'dashboard') {
    const widgets = report.widgets.flatMap((item) =>
      item.type === 'stack' ? item.widgets : [item],
    )
    // Liste ve yaşlandırma aynı sütun filtresi durumunu paylaşır.
    dimensions = widgets.flatMap((widget) =>
      widget.type === 'list' || widget.type === 'aging' ? widget.dimensions : [],
    )
  }
  return new Map(dimensions.map((field) => [field.name, filterKind(field)]))
}

/** Filtre durumunun okunacağı kaynak: rapor ya da (keşfet sayfası gibi) yalnız filtre listesi. */
export interface FilterSource {
  filters: (ReportFilterDef | PeriodFilterDef)[]
  columnKinds?: ReadonlyMap<string, FilterFieldKind>
}

/** Meta'daki filtre varsayılanları, adres çubuğu parametresi olarak. */
export function defaultFilterParams(source: {
  filters: (ReportFilterDef | PeriodFilterDef)[]
}): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = {}
  for (const filter of source.filters) {
    if (filter.type === 'period') continue // period: usePeriod, adrese mutlak yazılır
    if (typeof filter.default === 'string') {
      const { from, to } = resolveDefaultRange(filter.default)
      if (from) params.date_from = from
      if (to) params.date_to = to
    } else if (filter.default) {
      params[shortName(filter.member)] = filter.default
    }
  }
  return params
}

/**
 * Filtre durumu adres çubuğunda yaşar (bkz. `useQueryState`). Parametre adları:
 * `date_from`/`date_to`, `q`, `cf` (sütun filtreleri) ve diğer filtreler için üyenin kısa adı
 * (`kantarId`). Her filtre değişimi sayfayı başa alır.
 *
 * Meta varsayılanları: adres çubuğunda hiç filtre yokken geçerlidir. İlk
 * değişiklikte varsayılanlar + değişiklik birlikte yazılır ve `f=1` işaretlenir;
 * böylece "Tümü" seçimi (parametre yok) varsayılana geri dönmez. Temizle -> varsayılan.
 *
 * `period` filtresi (belgeler): adreste mutlak `?<üye>=2026-09`. Adres boşsa (ya da
 * geçersizse) göreli `default` bugüne (İstanbul) göre çözülüp adrese mutlak yazılır; bu
 * "dokunma" sayılmaz, diğer varsayılanlar korunur. Sorguya `periodRange` ile
 * `dateFrom`/`dateTo` olarak gider.
 */
export function useReportFilters(
  source: ReportDef | FilterSource,
  options: {
    /**
     * Meta'da olmayan, sayfanın kendi filtre parametreleri (mutabakatın `kantar`, saatleri):
     * değişince sayfa başa döner, "Filtreleri temizle" onları da siler. Sabit dizi verin.
     */
    extraKeys?: readonly string[]
  } = {},
) {
  const { extraKeys = NO_EXTRA_KEYS } = options
  const query = useQueryState()
  const kinds = useMemo(
    () => ('layout' in source ? columnFilterKinds(source) : (source.columnKinds ?? new Map())),
    [source],
  )
  const defaults = useMemo(() => defaultFilterParams(source), [source])

  const periodFilter = (source.filters as (ReportFilterDef | PeriodFilterDef)[]).find(
    (filter): filter is PeriodFilterDef => filter.type === 'period',
  )
  const periodKey = periodFilter && shortName(periodFilter.member)
  const periodDefault = periodFilter?.default
    ? resolveRelativePeriod(periodFilter.default)
    : undefined
  const periodParam = periodKey ? query.get(periodKey) : undefined
  const period = isValidPeriod(periodParam) ? periodParam : periodDefault

  useEffect(() => {
    if (periodKey && periodDefault && !isValidPeriod(periodParam)) {
      query.set({ [periodKey]: periodDefault })
    }
  }, [query, periodKey, periodDefault, periodParam])

  const filterKeys = useMemo(
    () => [
      'date_from',
      'date_to',
      'q',
      'cf',
      'x',
      ...source.filters
        .filter((filter) => filter.type !== 'dateRange')
        .map((filter) => shortName(filter.member)),
      ...extraKeys,
    ],
    [source, extraKeys],
  )

  const untouched =
    Object.keys(defaults).length > 0 &&
    query.get(TOUCHED_PARAM) === undefined &&
    filterKeys.every((key) => key === periodKey || query.get(key) === undefined)

  const get = useCallback(
    (key: string) => {
      const fallback = untouched ? defaults[key] : undefined
      return typeof fallback === 'string' ? fallback : query.get(key)
    },
    [untouched, defaults, query],
  )
  const getList = useCallback(
    (key: string) => {
      const fallback = untouched ? defaults[key] : undefined
      return Array.isArray(fallback) ? fallback : query.getList(key)
    },
    [untouched, defaults, query],
  )

  const columnsRaw = get('cf')
  const columns = useMemo(() => parseColumnFilters(columnsRaw, kinds), [columnsRaw, kinds])

  const selections = useMemo(() => parseSelections(getList('x')), [getList])

  const state: ReportFilterState = useMemo(() => {
    const multi: ReportFilterState['multi'] = {}
    const bool: ReportFilterState['bool'] = {}
    for (const filter of source.filters) {
      if (filter.type === 'multiSelect') multi[filter.member] = getList(shortName(filter.member))
      if (filter.type === 'boolean') {
        const raw = get(shortName(filter.member))
        bool[filter.member] = raw === undefined ? undefined : raw === 'true'
      }
    }
    const range = period ? periodRange(period) : undefined
    return {
      dateFrom: range ? range[0] : get('date_from'),
      dateTo: range ? range[1] : get('date_to'),
      multi,
      bool,
      q: get('q'),
      columns,
      selections,
    }
  }, [source, get, getList, columns, selections, period])

  const hasDefaults = Object.keys(defaults).length > 0

  /** Filtre değişikliği: sayfa numarası sıfırlanır. */
  const update = (patch: Record<string, string | string[] | undefined>) =>
    query.set({
      ...(untouched ? defaults : {}),
      ...patch,
      ...(hasDefaults ? { [TOUCHED_PARAM]: '1' } : {}),
      page: undefined,
    })

  const setSelections = (next: Selection[]) => update({ x: serializeSelections(next) })

  return {
    state,
    /** Seçili dönem (`YYYY-MM`); `period` filtresi yoksa tanımsız. */
    period,
    get,
    getList,
    update,
    setColumns: (next: ColumnFilters) => update({ cf: serializeColumnFilters(next) }),
    setSelections,
    /** Çapraz filtre tıklaması (Ctrl/Meta: `additive`). */
    select: (next: SelectionInput, additive: boolean) =>
      setSelections(toggleSelection(selections, next, additive)),
    /** Sayfalama/sıralama gibi filtre dışı parametreler. */
    set: query.set,
    reset: () => query.reset([...filterKeys, TOUCHED_PARAM, 'page']),
  }
}

export type ReportFilters = ReturnType<typeof useReportFilters>
